import { useEffect, useRef, useState } from 'react';
import type { MouseEvent } from 'react';
import type { AppointmentModel } from 'models/appointments';
import useAppointmentActions from 'hooks/useAppointmentActions';
import type { AppointmentActionsScope } from 'hooks/useAppointmentActions';
import ConfirmDialog from 'components/shared/ConfirmDialog';
import RescheduleForm from './RescheduleForm';

export const APPT_ACTIONS_HEADING = 'Manage appointment';
export const APPT_ACTIONS_TEAM_BADGE = 'Team action — bypasses customer rules';
export const APPT_ACTIONS_RESCHEDULE_LABEL = 'Reschedule appointment';
export const APPT_ACTIONS_CANCEL_LABEL = 'Cancel appointment';
export const APPT_ACTIONS_CANCEL_TITLE = 'Cancel appointment';
export const APPT_ACTIONS_CANCEL_MESSAGE =
  'This will cancel your scheduled consultation. This action cannot be undone.';
export const APPT_ACTIONS_CANCEL_CONFIRM_LABEL = 'Yes, cancel appointment';
export const APPT_ACTIONS_CANCELLED_BANNER = 'This appointment has been cancelled.';

type Props = {
  /** Parsed completed-Consultation output. When `submissionId` is undefined
   *  (legacy consultations / pre-merge backend) the panel renders nothing —
   *  graceful degradation per Req 7, never an error. */
  appointment: AppointmentModel;
  /** 'customer' (default) calls /v1/me endpoints; 'team' calls the rule-bypass
   *  team endpoints and renders a visually distinct badge. */
  scope?: AppointmentActionsScope;
  /** Required when scope is 'team'. */
  teamId?: string;
  /** Renders a cancelled banner and hides all actions. */
  isCancelled?: boolean;
  /** Awaited after a successful mutation — re-fetches so the refetched
   *  outputJson stays the display source of truth (plan Decision 5). */
  onRefresh: () => Promise<void> | void;
};

/**
 * Cancel/reschedule actions for a scheduled consultation appointment. All
 * mutation state lives in `useAppointmentActions`; this component owns only UI
 * state (inline reschedule form, cancel dialog, focus restore). Cancel flows
 * through the shared ConfirmDialog and fires only on confirm; the dialog
 * closes on success AND on failure so the hook's status-mapped error (422
 * reason verbatim) in the panel's role="alert" is never hidden behind the
 * modal backdrop. The reschedule form closes only on success — on failure the
 * same error renders in place and input is preserved.
 */
export default function AppointmentActionsPanel({
  appointment,
  scope = 'customer',
  teamId,
  isCancelled = false,
  onRefresh,
}: Props) {
  const { cancelAppointment, rescheduleAppointment, isCancelling, isRescheduling, error } =
    useAppointmentActions({ scope, teamId, onRefresh });
  const [rescheduleOpen, setRescheduleOpen] = useState<boolean>(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState<boolean>(false);
  const [focusRestoreCount, setFocusRestoreCount] = useState<number>(0);
  const rescheduleTriggerRef = useRef<HTMLButtonElement | null>(null);

  // Return focus to the Reschedule trigger after the form close has been
  // committed (the trigger may have been disabled during the mutation).
  useEffect(() => {
    if (focusRestoreCount === 0) return;
    rescheduleTriggerRef.current?.focus();
  }, [focusRestoreCount]);

  // Graceful degradation: no submissionId → no actions (hook above never fetches).
  if (!appointment.submissionId) return null;
  const submissionId = appointment.submissionId;

  const openReschedule = (e: MouseEvent<HTMLButtonElement>): void => {
    rescheduleTriggerRef.current = e.currentTarget;
    setRescheduleOpen(true);
  };

  const closeReschedule = (): void => {
    setRescheduleOpen(false);
    setFocusRestoreCount((n) => n + 1);
  };

  const handleRescheduleSave = async (newAppointmentTimeUtc: string): Promise<void> => {
    const ok = await rescheduleAppointment(submissionId, newAppointmentTimeUtc);
    if (ok) closeReschedule();
  };

  const handleCancelConfirm = async (): Promise<void> => {
    // Close on success and on failure: on failure the hook's status-mapped
    // error renders in the panel's role="alert", which the open dialog's
    // modal backdrop would otherwise obscure.
    await cancelAppointment(submissionId);
    setCancelDialogOpen(false);
  };

  return (
    <div className="mt-3 space-y-3 rounded-lg border border-gray-300 bg-white px-3 py-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          {APPT_ACTIONS_HEADING}
        </p>
        {scope === 'team' && (
          <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
            {APPT_ACTIONS_TEAM_BADGE}
          </span>
        )}
      </div>

      {isCancelled ? (
        <p className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm font-medium text-gray-600">
          {APPT_ACTIONS_CANCELLED_BANNER}
        </p>
      ) : (
        <>
          {error && !rescheduleOpen && (
            <p role="alert" className="text-sm text-red-600">
              {error}
            </p>
          )}

          {rescheduleOpen ? (
            <RescheduleForm
              currentTimeUtc={appointment.appointmentTimeUtc}
              isSubmitting={isRescheduling}
              serverError={error}
              onSave={(value) => void handleRescheduleSave(value)}
              onCancel={closeReschedule}
            />
          ) : (
            <div className="flex gap-2">
              <button
                type="button"
                onClick={openReschedule}
                disabled={isCancelling}
                className="inline-flex items-center justify-center rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {APPT_ACTIONS_RESCHEDULE_LABEL}
              </button>
              <button
                type="button"
                onClick={() => setCancelDialogOpen(true)}
                disabled={isCancelling || isRescheduling}
                className="inline-flex items-center justify-center rounded-lg border border-red-300 bg-white px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {APPT_ACTIONS_CANCEL_LABEL}
              </button>
            </div>
          )}
        </>
      )}

      <ConfirmDialog
        isOpen={cancelDialogOpen}
        title={APPT_ACTIONS_CANCEL_TITLE}
        message={APPT_ACTIONS_CANCEL_MESSAGE}
        confirmLabel={APPT_ACTIONS_CANCEL_CONFIRM_LABEL}
        isDestructive
        isConfirming={isCancelling}
        onConfirm={() => void handleCancelConfirm()}
        onCancel={() => setCancelDialogOpen(false)}
      />
    </div>
  );
}
